import { ModalType } from 'const';
import { CommonWindowOptions } from 'models/CommonWindowOptions';
import { isImmutable } from 'utils/immutable';

/**
 * Structure of a modal window
 */
interface ModalWindow {
  type: ModalType;
  options: CommonWindowOptions;
}

/**
 * Map of modal windows where the key is the modal ID
 */
interface ModalWindowsMap {
  [key: string]: ModalWindow;
}

/**
 * Result of finding a modal in the modal windows map
 */
export interface FoundModal {
  id: string;
  window: ModalWindow;
}

/**
 * Type for the modal windows map from Redux state
 */
export type ModalWindowsStateMap = DeepIMap<{ [modalId: string]: { type: ModalType; options: CommonWindowOptions } }>;

/**
 * Safely finds a modal by type from the Immutable.js modal windows map
 * @param modalWindowsMap The Immutable.js map of modal windows
 * @param modalType The type of modal to find
 * @returns A FoundModal object or undefined if not found
 */
export function findModalByType(
  modalWindowsMap: ModalWindowsStateMap,
  modalType: ModalType,
): FoundModal | undefined {
  if (!isImmutable(modalWindowsMap)) {
    return undefined;
  }
  try {
    const modalWindows = modalWindowsMap.toJS() as ModalWindowsMap;

    const matchingModalEntry = Object.entries(modalWindows).find(
      ([, modal]) => modal?.type === modalType,
    );

    if (!matchingModalEntry) {
      return undefined;
    }

    const [id, window] = matchingModalEntry;

    return { id, window };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(error);
    }

    return undefined;
  }
}
